import { useRef, type ChangeEvent } from 'react';
import { Button, Stack } from '@mui/material';
import { useFormContext } from 'FrontRoyalReactHookForm';
import { showToast } from 'Toast';
import { SectionHeading, messageFrom } from 'ContentItemEditor';
import { buildStreamMetadataCsv, parseStreamMetadataCsv } from './streamMetadataCsv';
import { type StreamFormValues } from './StreamEditor.types';

// The fields an import may write. A template literal, because the chapter and highlight rows are
// addressed by index.
type FieldName = 'tag' | 'title' | 'description' | `chapters.${number}.title` | `what_you_will_learn.${number}.value`;

function downloadCsv({ filename, content }: { filename: string; content: string }) {
    const blob = new Blob([content], { type: 'data:text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
}

/*
    The course metadata as a CSV for a translator, and the translated file back again.

    Import fills the form rather than the record, so an admin sees what arrived and can still change
    it or leave without saving. The Angular version wrote straight onto the model.
*/
export default function MetadataCsvButtons() {
    const { getValues, setFieldValue } = useFormContext<StreamFormValues>();
    const inputRef = useRef<HTMLInputElement>(null);

    function exportMetadata() {
        downloadCsv(buildStreamMetadataCsv(getValues()));
    }

    async function importMetadata(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];

        // Clear the input so the same file can be imported twice.
        event.target.value = '';
        if (!file) return;

        try {
            const values = getValues();
            const imported = parseStreamMetadataCsv(await file.text(), {
                chapterCount: values.chapters.length,
                learningGoalCount: values.what_you_will_learn.length,
            });

            // A row the translator left blank keeps the value the course already has, rather than
            // clearing it.
            let applied = 0;
            const apply = (field: FieldName, value: string | undefined) => {
                if (value === undefined) return;

                setFieldValue(field, value);
                applied += 1;
            };

            apply('tag', imported.tag);
            apply('title', imported.title);
            apply('description', imported.description);
            imported.chapterTitles.forEach((title, index) => apply(`chapters.${index}.title`, title));
            imported.learningGoals.forEach((goal, index) => apply(`what_you_will_learn.${index}.value`, goal));

            if (applied === 0) {
                showToast({
                    message: 'Nothing was imported',
                    description: 'The Translation column of that file is empty.',
                    type: 'danger',
                });
                return;
            }

            showToast({
                message: `${applied} field(s) imported. Save the course to keep it.`,
                type: 'success',
            });
        } catch (error) {
            // Either an unreadable file, which the Angular version swallowed without a word after
            // hitting it with Arabic files, or one that is not a metadata export.
            showToast({
                message: 'That file could not be imported',
                description: messageFrom(error, 'The file could not be read.'),
                type: 'danger',
            });
        }
    }

    return (
        <Stack spacing={1}>
            <SectionHeading>Course metadata</SectionHeading>

            <Stack direction="row" spacing={1}>
                <Button variant="contained" color="primary" onClick={exportMetadata}>
                    Create
                </Button>

                <Button variant="contained" color="primary" onClick={() => inputRef.current?.click()}>
                    Import
                </Button>
            </Stack>

            <input ref={inputRef} type="file" accept="text/csv" onChange={importMetadata} style={{ display: 'none' }} />
        </Stack>
    );
}
